import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import importAsset from 'lumen/helpers/import-asset';
import type Highlight from 'lumen/models/roi-trophies/highlight';
import {
  HIGHLIGHT_PERIOD_TYPE,
  type RevenueGeneralData,
} from 'lumen/models/roi-trophies/highlight';
import lottieAnimation from 'lumen/modifiers/lottie-animation';

import GeneralRoiTrophiesCountUpNumber from '../count-up-number';
import GeneralRoiTrophiesContentTrophyModalLayout from './trophy-modal-layout';

interface GeneralRoiTrophiesContentRevenueGeneralSignature {
  Args: {
    highlight: Highlight;
  };
}

export default class GeneralRoiTrophiesContentRevenueGeneral extends Component<GeneralRoiTrophiesContentRevenueGeneralSignature> {
  @service declare dates: Services['dates'];

  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  @service declare intl: Services['intl'];

  get currency(): string {
    return this.hotelAndMarketService.hotel!.currencyCode;
  }

  get data(): RevenueGeneralData {
    return this.args.highlight.highlightData as RevenueGeneralData;
  }

  get subline(): string {
    const { periodType } = this.args.highlight;
    const date = this.args.highlight.periodStart!; // always present for this highlight type

    const { partialDateFormat } = this.dates;

    if (periodType === HIGHLIGHT_PERIOD_TYPE.DAY) {
      return this.intl.t('roi_trophies.revenue.subline', {
        date: dayjs(date).format(
          partialDateFormat.WEEKDAY_AND_YEAR_MONTH_AND_DAY_SHORT,
        ),
      });
    }

    if (periodType === HIGHLIGHT_PERIOD_TYPE.MONTH) {
      return this.intl.t('roi_trophies.revenue.subline', {
        date: dayjs(date).format(partialDateFormat.YEAR_AND_MONTH_LONG),
      });
    }

    // Default: WEEK
    return this.intl.t('roi_trophies.revenue.subline_for_week', {
      date: dayjs(date).format(partialDateFormat.MONTH_AND_DAY_SHORT),
    });
  }

  <template>
    <GeneralRoiTrophiesContentTrophyModalLayout>
      <:content>
        <div class="flex flex-col items-center gap-300">
          <div>
            <div class="heading-1">
              <GeneralRoiTrophiesCountUpNumber
                @currency={{this.currency}}
                @target={{this.data.revenue}}
              />
            </div>
            <p data-test-id="revenue-general-subline">{{this.subline}}</p>
          </div>
          <p class="text-300--regular text-neutral-subdued">{{t
              "roi_trophies.revenue_general.body"
            }}</p>
        </div>
      </:content>
      <:animation>
        <div
          {{lottieAnimation
            (importAsset "animations/roi-trophies/record.json")
            false
          }}
        />
      </:animation>
    </GeneralRoiTrophiesContentTrophyModalLayout>
  </template>
}
